// Differentiator #1: Die Suche IST die Startseite, kein Kachel-Menue. Typeahead ueber
// Equipment (Cache-Tier -> sofort). Eine Auswahl reicht das Equipment nach oben durch —
// auf der Startseite startet das einen Auftrag.
//
// Portierung aus dem Vite-Prototyp (frontend/src/components/SearchBar.jsx). Geaendert wurden
// NUR Styling-Idiom (v4-Token/.gx-* -> isDarkMode-Verzweigung mit konkreten Tailwind-v3-Klassen),
// Theme-/i18n-Hook und Import-Pfade sowie die Monolith-API-Signatur:
//   - useI18n('../i18n') -> useLanguage(core), Keys mit pm.-Praefix.
//   - api.searchEquipment(q) -> searchEquipment(user, q): im Monolithen ist `user` das
//     ERSTE Argument (traegt das JWT) und die Funktion liefert den geparsten JSON-Body
//     DIREKT zurueck (kein { data }-Wrapper wie im Prototyp) — daher `res?.items`.
import React, { useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';
import { searchEquipment } from '../api';
import { recordRecent } from '../recency';

function SearchBar({ user, role, onSelect, autoFocus = false }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef();
  // seq-Guard: der Debounce-Timer wird zwar bei jedem Tastendruck neu gesetzt, aber ein bereits
  // GESTARTETER Fetch der alten Query kann nach dem neuen zurueckkommen — nur die juengste zaehlt.
  const seq = useRef(0);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!q.trim()) { setResults([]); return; }
    timer.current = setTimeout(() => {
      const mySeq = ++seq.current;
      // Monolith-API: `user` zuerst, Antwort ist direkt der JSON-Body (.items).
      searchEquipment(user, q.trim(), role)
        .then((res) => {
          if (seq.current !== mySeq) return;
          setResults(res?.items || []);
          setOpen(true);
        })
        .catch(() => {
          // Fehler still schlucken waere eine tote Suche ohne Hinweis — Dropdown schliessen und
          // Ergebnisse leeren reicht hier (die Typeahead erholt sich mit dem naechsten Tastendruck).
          if (seq.current !== mySeq) return;
          setResults([]);
          setOpen(false);
        });
    }, 180);
    return () => timer.current && clearTimeout(timer.current);
  }, [q, user, role]);

  return (
    <div className="relative">
      {/* Suchfeld — entspricht .gx-field: Glas (dunkel) / klar (hell). */}
      <div
        className={`flex items-center gap-2 px-4 min-h-[60px] rounded-xl border transition-all duration-200 ${isDarkMode
          ? 'bg-white/[0.08] border-white/15 text-white/80 hover:bg-white/[0.12]'
          : 'bg-white border-slate-200 text-slate-700 hover:border-brand/40'
        }`}
      >
        <Search className={`shrink-0 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`} />
        <input
          autoFocus={autoFocus}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          placeholder={t('pm.search.placeholder')}
          className={`flex-1 bg-transparent outline-none text-lg py-3 min-w-0 ${isDarkMode ? 'text-white/80' : 'text-slate-900'}`}
        />
        {q && (
          <button
            onClick={() => { setQ(''); setResults([]); }}
            className={`shrink-0 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}
            aria-label={t('pm.common.clear')}
          >
            <X />
          </button>
        )}
      </div>

      {/* Ergebnis-Dropdown — entspricht .gx-card: Glas-Container ueber dem Feld. */}
      {open && results.length > 0 && (
        <div
          className={`absolute z-20 mt-2 w-full overflow-hidden rounded-3xl ${isDarkMode
            ? 'border border-white/10 bg-gradient-to-br from-gray-900/40 via-gray-900/20 to-gray-900/40 backdrop-blur-2xl shadow-lg'
            : 'glass-light-card'
          }`}
        >
          {results.map((eq) => (
            <button
              key={eq.equipmentId}
              onClick={() => {
                setOpen(false);
                // Nutzung verbuchen (Zuletzt-verwendet, fire-and-forget) — jede Auswahl
                // aus der Typeahead zaehlt, egal von welchem Screen aus gesucht wurde.
                recordRecent(user, 'equipment', eq, 'equipmentId');
                onSelect(eq);
              }}
              className={`w-full text-left px-4 py-3 border-b last:border-0 transition-colors ${isDarkMode
                ? 'hover:bg-white/[0.08] border-white/10'
                : 'hover:bg-slate-50 border-slate-200'
              }`}
            >
              <div className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                {eq.description}
              </div>
              <div className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
                {eq.equipmentId} · {eq.functionalLocation}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default SearchBar;
